import * as Sentry from "@sentry/react";
import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from "react-router";

import { AppRedirectRoute } from "AppRedirectRoute";
import {
  ProtectedBookmarksPage,
  ProtectedCreateOrEditCollectionPage,
  ProtectedModerateRoute,
  ProtectedMonitorPage,
  ProtectedPage,
  ProtectedSourcesPage,
  ProtectedTopicDetailPage,
} from "common/components/ProtectedPage";
import { DownloadMobileApp } from "domains/about/components/DownloadMobileApp";
import { ClusterPage } from "domains/clusters/components/ClusterPage";
import { DigDeeperPage } from "domains/clusters/components/DigDeeperPage";
import { ProtectedEditCollectionPage } from "domains/collection/components/edit/ProtectedEditCollectionPage";
import { LoadCollectionPage } from "domains/collection/components/LoadCollectionPage";
import { CreateLanding } from "domains/create/landing/CreateLanding";
import { FollowedTopicsPage } from "domains/customTopicGrid/FollowedTopicsPage";
import { SharedTopicsPage } from "domains/customTopicGrid/SharedTopicsPage";
import { DiscoverPage } from "domains/discover/components/DiscoverPage";
import { TopicEventDataPage } from "domains/events/components/TopicEventPage";
import { FAQs } from "domains/faqs/components/FAQs";
import { TopicInitiativePage } from "domains/initiatives/components/TopicInitiativePage";
import { TopicKnowledgeDataPage } from "domains/knowledgeData/components/TopicKnowledgeDataPage";
import { Landing } from "domains/landing/Landing";
import { UpcomingEventsPage } from "domains/membershipEvents/components/MembershipEventsPage";
import { SourcesPage } from "domains/moderate/sources/components/SourcesPage/SourcesPage";
import { LatestKnowledgePage } from "domains/monitor/latestKnowledge/components/LatestKnowledgePage";
import { Monitor } from "domains/monitor/Monitor";
import { PageNotFound } from "domains/pageNotFound/PageNotFound";
import { TopicPublicationPage } from "domains/publication/components/TopicPublicationPage";
import { ResourcesPage } from "domains/resources/ResourcesPage";
import { InteractiveDataSearchPage } from "domains/search/components/interactiveData/InteractiveDataSearchPage";
import { SearchKnowledgePage } from "domains/search/components/publications/PublicationsSearchPage";
import { SearchPage } from "domains/search/components/SearchPage";
import { VideoSearchPage } from "domains/search/components/videos/VideosSearchPage";
import { TopicStakeholderPage } from "domains/stakeholders/components/TopicStakeholderPage";
import { TermsAndConditions } from "domains/termsAndConditions/components/TermsAndConditions";
import { TopicDetailOutlet } from "domains/topicDetail/components/TopicDetailOutlet";
import { TopicDetailPage } from "domains/topicDetail/components/TopicDetailPage";
import {
  LoginOrRegisterEffect,
  LogoutEffect,
  MapInviteLinkEffect,
  NotificationsEffect,
  ResetPasswordLoginEffect,
} from "domains/user/components/UserRoutes";
import { TopicVideoPage } from "domains/videos/components/TopicVideoPage";
import { WhatsNewRoute } from "domains/whatsNew/components/WhatsNewRoute";
import { Lazy } from "router/Lazy";
import { RootOutlet } from "router/RootOutlet";
import { SetColorPrimary } from "router/SetColorPrimary";

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const createRouter = () => sentryCreateBrowserRouter(createRoutes());

const createRoutes = () =>
  createRoutesFromElements(
    <Route path="" element={<RootOutlet />}>
      <Route index element={<Landing />} />
      <Route path="about" element={<Lazy load={() => import("../domains/about/components/About")} />} />
      <Route path="app" element={<DownloadMobileApp />} />
      <Route path="app-redirect" element={<AppRedirectRoute />} />
      <Route path="bookmarks/:bookmarkId?" element={<ProtectedBookmarksPage />} />
      <Route path="briefings" element={<Lazy load={() => import("../domains/briefings/BriefingsPage")} />} />
      <Route path="collection">
        <Route index element={<ProtectedCreateOrEditCollectionPage />} />
        <Route path=":collectionId">
          <Route index element={<LoadCollectionPage />}></Route>
          <Route path="edit" element={<ProtectedEditCollectionPage />} />
        </Route>
      </Route>
      <Route path="cookie-notice" element={<TermsAndConditions type="cookie-notice" />} />
      <Route path="create">
        <Route index element={<CreateLanding />} />
        <Route path="new" element={<TopicDetailOutlet />}>
          <Route index element={<ProtectedTopicDetailPage />} />
        </Route>
        <Route path=":topicId" element={<TopicDetailOutlet />}>
          {topicRoutes}
        </Route>
      </Route>
      <Route path="discover">
        <Route index element={<DiscoverPage />} />
        <Route path=":topicId" element={<TopicDetailOutlet />}>
          {topicRoutes}
        </Route>
      </Route>
      <Route path="download-app" element={<DownloadMobileApp />} />
      <Route path="faq">
        <Route index element={<Navigate to="/faq/general" replace />} />
        <Route path="access" element={<FAQs type="access" />} />
        <Route path="general" element={<FAQs type="general" />} />
        <Route path="membership" element={<FAQs type="membership" />} />
        <Route path="payments-and-billing" element={<FAQs type="payments-and-billing" />} />
      </Route>
      <Route
        path="followed"
        element={
          <ProtectedPage>
            <FollowedTopicsPage />
          </ProtectedPage>
        }
      />
      <Route path="login" element={<LoginOrRegisterEffect />} />
      <Route path="logout" element={<LogoutEffect />} />
      <Route path="map-invite-link/:token" element={<MapInviteLinkEffect />} />
      <Route
        path="membership-events/:eventId?"
        element={
          <SetColorPrimary>
            <UpcomingEventsPage />
          </SetColorPrimary>
        }
      />
      <Route path="monitor">
        <Route
          index
          element={
            <SetColorPrimary>
              <Monitor />
            </SetColorPrimary>
          }
        />
        <Route path="emerging-themes">
          <Route
            path=":clusterId?"
            element={
              <SetColorPrimary>
                <ClusterPage />
              </SetColorPrimary>
            }
          />
          <Route
            path=":clusterId/dig-deeper/:id?"
            element={
              <SetColorPrimary>
                <ProtectedMonitorPage>
                  <DigDeeperPage />
                </ProtectedMonitorPage>
              </SetColorPrimary>
            }
          />
        </Route>
        <Route
          path="latest-knowledge/:id?"
          element={
            <SetColorPrimary>
              <LatestKnowledgePage />
            </SetColorPrimary>
          }
        />
      </Route>
      <Route path="news" element={<WhatsNewRoute />} />
      <Route path="notifications/:id?" element={<NotificationsEffect />} />
      <Route path="privacy-policy" element={<TermsAndConditions type="privacy-policy" />} />
      <Route path="register" element={<LoginOrRegisterEffect />} />
      <Route path="reset-password-login" element={<ResetPasswordLoginEffect />} />
      <Route path="resources" element={<ResourcesPage />} />
      <Route path="search">
        <Route index element={<SearchPage />} />
        <Route path="interactive-data/:dataId?" element={<InteractiveDataSearchPage />} />
        <Route path="publications/:knowledgeId?" element={<SearchKnowledgePage />} />
        <Route path="videos/:videoId?" element={<VideoSearchPage />} />
      </Route>
      <Route
        path="shared"
        element={
          <ProtectedPage>
            <SharedTopicsPage />
          </ProtectedPage>
        }
      />
      <Route path="terms-and-conditions" element={<TermsAndConditions type="terms-and-conditions" />} />
      <Route path="topics">
        <Route index element={<DiscoverPage />} />
        <Route path=":topicId" element={<TopicDetailOutlet />}>
          {topicRoutes}
        </Route>
      </Route>

      <Route element={<ProtectedModerateRoute />}>
        <Route path="moderate">
          <Route
            path="sources/:sourceId?"
            element={
              <ProtectedSourcesPage>
                <SourcesPage />
              </ProtectedSourcesPage>
            }
          />
        </Route>
      </Route>

      <Route path="*" element={<PageNotFound />} />
    </Route>,
  );

const topicRoutes = (
  <>
    <Route index element={<TopicDetailPage />} />
    <Route path="briefing" element={<TopicDetailPage launchBriefing />} />
    <Route path="events/:eventId?" element={<TopicEventDataPage />} />
    <Route path="initiatives/:initiativeId?" element={<TopicInitiativePage />} />
    <Route path="interactive-data/:knowledgeId?" element={<TopicKnowledgeDataPage />} />
    <Route path="publications/:knowledgeId?" element={<TopicPublicationPage />} />
    <Route path="stakeholders/:stakeholderId?" element={<TopicStakeholderPage />} />
    <Route path="videos/:knowledgeId?" element={<TopicVideoPage />} />
    <Route path="key-issues/:keyIssueId" element={<TopicDetailPage />} />
    <Route path="key-issues/:keyIssueId/briefing" element={<TopicDetailPage launchBriefing />} />
    <Route path="key-issues/:keyIssueId/events/:eventId?" element={<TopicEventDataPage />} />
    <Route path="key-issues/:keyIssueId/initiatives/:initiativeId?" element={<TopicInitiativePage />} />
    <Route path="key-issues/:keyIssueId/interactive-data/:knowledgeId?" element={<TopicKnowledgeDataPage />} />
    <Route path="key-issues/:keyIssueId/publications/:knowledgeId?" element={<TopicPublicationPage />} />
    <Route path="key-issues/:keyIssueId/stakeholders/:stakeholderId?" element={<TopicStakeholderPage />} />
    <Route path="key-issues/:keyIssueId/videos/:knowledgeId?" element={<TopicVideoPage />} />
  </>
);

export const router = createRouter();
